import React, { useEffect } from 'react';
import { Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { radius, shadows, spacing, type, useAppTheme } from '@/theme';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { dismissToast, ToastMessage } from '@/store/slices/uiSlice';
import { ScaledSheet } from '@/theme/responsive';

const iconMap: Record<ToastMessage['type'], keyof typeof Ionicons.glyphMap> = {
  success: 'checkmark-circle',
  error: 'close-circle',
  info: 'information-circle',
  warning: 'warning',
};

/** Mounted once in the root layout — reads the toast queue from Redux and auto-dismisses
 *  each message after 3s. Every screen dispatches `showToast(...)` instead of rendering
 *  its own transient message. */
export function ToastHost() {
  const toasts = useAppSelector((state) => state.ui.toasts);
  const dispatch = useAppDispatch();
  const insets = useSafeAreaInsets();

  return (
    <View pointerEvents="box-none" style={[styles.container, { top: insets.top + spacing.sm }]}>
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={() => dispatch(dismissToast(toast.id))} />
      ))}
    </View>
  );
}

function ToastItem({ toast, onDismiss }: { toast: ToastMessage; onDismiss: () => void }) {
  const { colors, isDark } = useAppTheme();

  useEffect(() => {
    const timer = setTimeout(onDismiss, 3000);
    return () => clearTimeout(timer);
  }, [toast.id, onDismiss]);

  const colorMap: Record<ToastMessage['type'], string> = {
    success: colors.success,
    error: colors.error,
    info: colors.info,
    warning: colors.warning,
  };

  return (
    <View
      style={[
        styles.toast,
        {
          backgroundColor: isDark ? colors.surface : '#0F172A',
          borderColor: isDark ? colors.border : 'rgba(255,255,255,0.1)',
        },
      ]}>
      <Ionicons name={iconMap[toast.type]} size={18} color={colorMap[toast.type]} />
      <Text style={[type.bodyMedium, styles.text, { color: isDark ? colors.textPrimary : '#F8FAFC' }]} numberOfLines={2}>
        {toast.message}
      </Text>
    </View>
  );
}

const styles = ScaledSheet.create({
  container: { position: 'absolute', left: spacing.lg, right: spacing.lg, gap: spacing.sm, zIndex: 999 },
  toast: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    borderRadius: radius.md,
    borderWidth: 1,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    ...shadows.elevated,
  },
  text: { flex: 1 },
});
